<div class="glass-card border border-white border-opacity-10 p-3 rounded-4">
    <div class="mb-3 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
        <div>
            <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                <h4 class="text-white fw-bold mb-0">Tariff Book</h4>
                <span class="badge rounded-pill settings-soft-badge settings-soft-badge-info">
                    {{ tariff_books.length }} tariff books
                </span>
            </div>
            <p class="text-white-50 mb-0 text-sm">
                Open a tariff book to inspect the tariff rates attached to it.
            </p>
        </div>
    </div>

    <div v-if="!tariff_book_rows.length" class="text-center text-white py-4 text-xs">
        <div class="d-flex flex-column align-items-center justify-content-center py-4">
            <span class="d-inline-flex align-items-center justify-content-center rounded-circle border border-white border-opacity-10 bg-white bg-opacity-10 text-white mb-3" style="width: 2.5rem; height: 2.5rem;">
                <i class="bi bi-journal-text"></i>
            </span>
            <p class="text-white fw-semibold mb-1">No tariff books available</p>
            <p class="text-white text-xs mb-0">Tariff books and their rates will appear here once loaded.</p>
        </div>
    </div>

    <div v-else class="d-flex flex-column gap-3">
        <div
            v-for="tariff_book in tariff_book_rows"
            :key="tariff_book.id"
            class="settings-accordion-item border border-white border-opacity-10 rounded-4 overflow-hidden"
        >
            <button
                type="button"
                class="settings-accordion-trigger w-100 border-0 bg-transparent text-start p-3"
                @click="toggle_tariff_book(tariff_book.id)"
                :aria-expanded="is_tariff_book_open(tariff_book.id) ? 'true' : 'false'"
            >
                <div class="d-flex justify-content-between align-items-start gap-3">
                    <div class="d-flex flex-column gap-2">
                        <div class="d-flex flex-wrap align-items-center gap-2">
                            <span class="text-white fw-semibold text-sm">{{ tariff_book.name || "Unnamed Tariff Book" }}</span>
                            <span class="badge rounded-pill settings-soft-badge text-xs">
                                {{ tariff_book.tariff_category || "-" }}
                            </span>
                        </div>

                        <div class="d-flex flex-wrap gap-3">
                            <span class="text-white-50 text-xs">Tariff Book ID {{ tariff_book.id }}</span>
                            <span class="text-white-50 text-xs">{{ tariff_book.period || "-" }}</span>
                            <span class="text-white-50 text-xs">
                                {{ tariff_book.tariff_total }} {{ tariff_book.tariff_total === 1 ? "tariff" : "tariffs" }}
                            </span>
                            <span class="text-white-50 text-xs">
                                {{ tariff_book.rate_total }} {{ tariff_book.rate_total === 1 ? "rate version" : "rate versions" }}
                            </span>
                            <span class="text-white-50 text-xs">
                                {{ format_date(tariff_book.effective_from) }} - {{ format_date(tariff_book.effective_to) }}
                            </span>
                        </div>
                    </div>

                    <div class="d-flex align-items-center gap-3">
                        <i
                            class="bi text-white-50"
                            :class="is_tariff_book_open(tariff_book.id) ? 'bi-chevron-up' : 'bi-chevron-down'"
                            aria-hidden="true"
                        ></i>
                    </div>
                </div>
            </button>

            <div v-if="is_tariff_book_open(tariff_book.id)" class="border-top border-white border-opacity-10 p-3 pt-0">
                <div class="table-responsive settings-table-shell settings-reference-table rounded-4 border border-white border-opacity-10 mt-3 d-none d-md-block">
                    <table
                        class="table table-dark table-hover align-middle mb-0 settings-data-table global_data_table text-xs"
                        data-search-placeholder="Search tariffs"
                        data-non-orderable-targets="[3]"
                        data-page-length="5"
                        data-order-column="0"
                        data-order-direction="asc"
                    >
                        <thead>
                            <tr>
                                <th scope="col" class="settings-table-head text-white fw-semibold text-xs">Tariff</th>
                                <th scope="col" class="settings-table-head text-white fw-semibold text-xs">Code</th>
                                <th scope="col" class="settings-table-head text-white fw-semibold text-xs">Latest charge components / History</th>
                                <th scope="col" class="settings-table-head text-white fw-semibold text-xs text-end">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="tariff in tariff_book.linked_tariffs" :key="`${tariff_book.id}-${tariff.id}`">
                                <td>
                                    <div class="d-flex flex-column gap-1">
                                        <span class="text-white fw-semibold text-xs">{{ tariff.name || "Unnamed tariff" }}</span>
                                        <span class="text-white-50 text-xs">{{ tariff.tariff_type || "Linked to selected tariff book" }}</span>
                                        <span class="text-white-50 text-xs">
                                            {{ tariff.rate_version_count }} {{ tariff.rate_version_count === 1 ? "dated version" : "dated versions" }}
                                        </span>
                                    </div>
                                </td>
                                <td class="text-white fw-semibold text-xs">{{ tariff.code || "-" }}</td>
                                <td>
                                    <div v-if="tariff.rate_versions.length" class="d-flex flex-column gap-1">
                                        <span class="text-white fw-semibold text-xs">
                                            Latest: {{ format_date(tariff.latest_rate_version?.effective_from) }} - {{ format_date(tariff.latest_rate_version?.effective_to) }}
                                        </span>
                                        <span class="text-white-50 text-xs">
                                            {{ tariff.latest_charge_component_count }} {{ tariff.latest_charge_component_count === 1 ? "charge component" : "charge components" }} editable
                                        </span>
                                        <span class="text-white-50 text-xs">
                                            {{ tariff.rate_versions.slice(0, 3).map((rate_version) => `${format_date(rate_version.effective_from)} to ${format_date(rate_version.effective_to)}`).join(" | ") }}
                                        </span>
                                    </div>
                                    <span v-else class="text-white-50 text-xs">No dated rate versions</span>
                                </td>
                                <td>
                                    <div class="d-flex justify-content-end gap-2 flex-wrap">
                                        <button
                                            type="button"
                                            class="btn-primary rounded-3 d-flex align-items-center gap-1 settings-button-shape settings-action-button"
                                            @click="$emit('view-rates', {
                                                tariff_book,
                                                context: {
                                                    tariff_book_id: tariff_book.id,
                                                    tariff_id: tariff.id,
                                                    tariff_name: tariff.name,
                                                    tariff_code: tariff.code,
                                                    tariff_type: tariff.tariff_type,
                                                    tariff_category: tariff.tariff_category,
                                                    property_name: tariff_book.name
                                                },
                                                tab: 'edit'
                                            })"
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </td>
                            </tr>

                            <tr v-if="!tariff_book.linked_tariffs.length">
                                <td colspan="4" class="text-center text-white py-4 text-xs">
                                    No tariffs linked to this tariff book.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="d-md-none mt-3">
                    <SettingsMobileListControls
                        v-if="tariff_book.linked_tariffs.length"
                        class="mb-3"
                        :search-value="get_mobile_tariff_search(tariff_book.id)"
                        search-placeholder="Search tariffs"
                        :current-page="get_mobile_tariff_page(tariff_book.id)"
                        :page-count="get_mobile_tariff_page_count(get_filtered_mobile_tariffs(tariff_book))"
                        pagination-label="Linked tariffs pages"
                        :show-pagination="false"
                        @update:search-value="set_mobile_tariff_search(tariff_book, $event)"
                        @update:current-page="set_mobile_tariff_page(tariff_book, $event)"
                    />

                    <div v-if="tariff_book.linked_tariffs.length" class="d-flex flex-column gap-3">
                        <article
                            v-for="tariff in get_paginated_mobile_tariffs(tariff_book)"
                            :key="`mobile-${tariff_book.id}-${tariff.id}`"
                            class="glass-card text-white border border-white border-opacity-10 rounded-4 shadow-sm"
                        >
                            <div class="card-body p-3">
                                <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
                                    <div class="min-w-0">
                                        <p class="text-uppercase text-primary fw-bold text-xs mb-2">Tariff</p>
                                        <h6 class="fw-semibold mb-1 text-break">{{ tariff.name || "Unnamed tariff" }}</h6>
                                        <p class="text-white-50 text-xs mb-0">{{ tariff.tariff_type || "Linked to selected tariff book" }}</p>
                                    </div>
                                    <span class="badge rounded-pill bg-primary text-white text-xs flex-shrink-0">
                                        {{ tariff.rate_version_count }} {{ tariff.rate_version_count === 1 ? "version" : "versions" }}
                                    </span>
                                </div>

                                <div class="row g-3">
                                    <div class="col-6">
                                        <p class="text-uppercase text-primary fw-bold text-xs mb-1">Code</p>
                                        <p class="fw-semibold text-xs mb-0">{{ tariff.code || "-" }}</p>
                                    </div>

                                    <div class="col-6">
                                        <p class="text-uppercase text-primary fw-bold text-xs mb-1">Components</p>
                                        <p class="fw-semibold text-xs mb-0">
                                            {{ tariff.latest_charge_component_count }} {{ tariff.latest_charge_component_count === 1 ? "component" : "components" }}
                                        </p>
                                    </div>

                                    <div class="col-12">
                                        <p class="text-uppercase text-primary fw-bold text-xs mb-1">Latest version</p>
                                        <p v-if="tariff.rate_versions.length" class="fw-semibold text-xs mb-0">
                                            {{ format_date(tariff.latest_rate_version?.effective_from) }} - {{ format_date(tariff.latest_rate_version?.effective_to) }}
                                        </p>
                                        <p v-else class="text-white-50 text-xs mb-0">No dated rate versions</p>
                                    </div>

                                    <div class="col-12">
                                        <p class="text-uppercase text-primary fw-bold text-xs mb-1">History</p>
                                        <p class="text-white-50 text-xs mb-0">
                                            {{ tariff.rate_versions.length ? tariff.rate_versions.slice(0, 3).map((rate_version) => `${format_date(rate_version.effective_from)} to ${format_date(rate_version.effective_to)}`).join(" | ") : "-" }}
                                        </p>
                                    </div>

                                    <div class="col-12">
                                        <p class="text-uppercase text-primary fw-bold text-xs mb-2">Actions</p>
                                        <button
                                            type="button"
                                            class="btn btn-primary btn-sm rounded-3 d-inline-flex align-items-center gap-2"
                                            @click="$emit('view-rates', {
                                                tariff_book,
                                                context: {
                                                    tariff_book_id: tariff_book.id,
                                                    tariff_id: tariff.id,
                                                    tariff_name: tariff.name,
                                                    tariff_code: tariff.code,
                                                    tariff_type: tariff.tariff_type,
                                                    tariff_category: tariff.tariff_category,
                                                    property_name: tariff_book.name
                                                },
                                                tab: 'edit'
                                            })"
                                        >
                                            <i class="bi bi-pencil-square"></i>
                                            <span class="text-xs">Edit</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </article>

                        <div v-if="!get_paginated_mobile_tariffs(tariff_book).length" class="glass-card text-white border border-white border-opacity-10 rounded-4">
                            <div class="card-body text-center py-4">
                                <p class="text-white-50 text-xs mb-0">No matching tariffs found.</p>
                            </div>
                        </div>

                        <SettingsMobileListControls
                            :search-value="get_mobile_tariff_search(tariff_book.id)"
                            :current-page="get_mobile_tariff_page(tariff_book.id)"
                            :page-count="get_mobile_tariff_page_count(get_filtered_mobile_tariffs(tariff_book))"
                            pagination-label="Linked tariffs pages"
                            :show-search="false"
                            @update:current-page="set_mobile_tariff_page(tariff_book, $event)"
                        />
                    </div>

                    <div v-else class="glass-card text-white border border-white border-opacity-10 rounded-4">
                        <div class="card-body text-center py-4">
                            <p class="text-white text-xs mb-0">No tariffs linked to this tariff book.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
